// src/components/admin/AdminGrades.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import schoolService from "../../services/schoolService";
import classService from "../../services/classService";
import semesterService from "../../services/semesterService";
import subjectService from "../../services/subjectService";
import gradeService from "../../services/gradeService";
import {
  getGradeDescription,
  formatGrade as gradeFormatter,
  getGradeColor,
} from "../../utils/gradeColors";

const AdminGrades = () => {
  const { user } = useAuth();

  // State
  const [schools, setSchools] = useState([]);
  const [userClasses, setUserClasses] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [classMembers, setClassMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState("");

  // Class Overview States
  // const [showClassOverview, setShowClassOverview] = useState(false);
  const [showClassOverview] = useState(false);
  const [classStatistics, setClassStatistics] = useState(null);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [academicYears] = useState([]);

  // Modal States
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [studentGrades, setStudentGrades] = useState([]);

  const getSubjectGrades = (subjectId) => {
    if (!grades) return [];
    return grades.filter((g) => String(g.subjectId) === String(subjectId));
  };

  const hasAccess = () =>
    user?.roles?.some((r) => r.name === "ADMIN" || r.name === "BERUFSBILDNER");

  // Init
  useEffect(() => {
    if (!hasAccess()) {
      setError(
        "Access denied. This feature is only available for administrators and instructors.",
      );
      setBootLoading(false);
      return;
    }
    (async () => {
      try {
        setError("");
        const schoolsResponse = await schoolService.getAllSchools();
        const schoolsData = Array.isArray(schoolsResponse)
          ? schoolsResponse
          : [];
        setSchools(schoolsData);
      } catch (e) {
        console.error("Error loading admin data:", e);
        setError("Error loading schools. Please try again later.");
      } finally {
        setBootLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.roles]);

  // Handlers
  const handleSchoolChange = async (schoolId) => {
    try {
      setLoading(true);
      setError("");
      const school =
        schools.find((s) => String(s.id) === String(schoolId)) || null;
      setSelectedSchool(school);
      setSelectedClass(null);
      setSemesters([]);
      setSubjects([]);
      setGrades([]);
      setClassMembers([]);
      setUserClasses([]);

      if (school) {
        const classesResponse = await classService.getClassesBySchool(
          school.id,
        );
        setUserClasses(classesResponse || []);
      }
    } catch (e) {
      console.error("Error loading classes:", e);
      setError("Error loading classes: " + (e.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = async (classId) => {
    if (!selectedSchool) return;
    const classObj =
      userClasses.find((c) => String(c.id) === String(classId)) || null;
    setSelectedClass(classObj);
    setSemesters([]);
    setSubjects([]);
    setGrades([]);
    setClassMembers([]);
    if (classObj) {
      await loadSemestersForClass(selectedSchool.id, classObj.id);
    }
  };

  const loadSemestersForClass = async (schoolId, classId) => {
    try {
      setLoading(true);
      const semestersResponse = await semesterService.getSemestersByClass(
        schoolId,
        classId,
      );
      const list = semestersResponse || [];
      setSemesters(list);
      if (list.length > 0) {
        const first = list.find((s) => s.isCurrent) || list[0];
        setSelectedSemester(first);
        await loadSubjectsAndGrades(schoolId, classId, first.id);
      }
    } catch (e) {
      console.error("Error loading semesters:", e);
      setError("Error loading semesters: " + (e.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const loadSubjectsAndGrades = async (schoolId, classId, semesterId) => {
    try {
      setLoading(true);
      setError("");
      const subjectsResponse =
        await subjectService.getSubjectsBySchoolClassSemester(
          schoolId,
          classId,
          semesterId,
        );
      setSubjects(subjectsResponse || []);

      // Get class members to filter grades
      const classMembersData = await classService.getClassMembers(classId);
      setClassMembers(classMembersData || []);

      // For Admin: Load grades for each class member individually
      // This is more reliable than trying to get all grades at once
      const allGrades = [];
      for (const member of classMembersData) {
        try {
          // const memberGrades = await gradeService.getMyGrades(semesterId); // This will get grades for current user
          // Since we need grades for each member, we should use a different approach
          // Let's try to get grades by user ID if available
          const userGrades = await gradeService.getGradesByUser(member.id);

          // Filter by semester (since getGradesByUser returns all grades for that user)
          const semesterGrades = userGrades.filter(
            (grade) => grade.semesterId === semesterId,
          );
          allGrades.push(...semesterGrades);
        } catch (error) {
          console.warn(`Failed to load grades for user ${member.id}:`, error);
          // Continue with next member
        }
      }

      const filteredGrades = allGrades;

      setGrades(filteredGrades);
    } catch (e) {
      console.error("Error loading subjects and grades:", e);
      setError(
        "Error loading subjects and grades: " + (e.message || "Unknown error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSemesterClick = async (semester) => {
    setSelectedSemester(semester);
    if (selectedSchool && selectedClass) {
      await loadSubjectsAndGrades(
        selectedSchool.id,
        selectedClass.id,
        semester.id,
      );
    }
  };

  // Calculations
  const calculateSubjectAverageMemo = useCallback(
    (subjectId) => {
      // getSubjectGrades inline logic
      const list = grades
        .filter((g) => g.subjectId === subjectId)
        .sort((a, b) => new Date(b.examDate) - new Date(a.examDate));

      if (!list.length) return null;
      const totalWeighted = list.reduce(
        (sum, g) => sum + Number(g.gradeValue) * (g.weight || 1),
        0,
      );
      const totalWeight = list.reduce((sum, g) => sum + (g.weight || 1), 0);
      if (!totalWeight) return null;
      return (totalWeighted / totalWeight).toFixed(2);
    },
    [grades],
  );

  const semesterAverage = useMemo(() => {
    const avgs = subjects
      .map((s) => calculateSubjectAverageMemo(s.id))
      .filter((x) => x !== null)
      .map(Number);
    if (!avgs.length) return null;
    const total = avgs.reduce((a, b) => a + b, 0);
    return (total / avgs.length).toFixed(2);
  }, [subjects, calculateSubjectAverageMemo]);

  // Load class statistics for overview
  const loadClassStatistics = useCallback(async () => {
    if (!selectedClass || !selectedSemester || !selectedAcademicYear) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const stats = await gradeService.getClassStatistics(
        selectedClass.id,
        selectedSemester.name,
        selectedAcademicYear,
      );
      setClassStatistics(stats);
    } catch (error) {
      console.error("Error loading class statistics:", error);
      setError("Error loading class statistics. Please try again.");
      setClassStatistics(null);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedSemester, selectedAcademicYear]);

  // Helper function to get user by ID using class members data
  const getUserById = useCallback(
    async (userId) => {
      try {
        // Look for user info in the class members data
        const member = classMembers.find((m) => m.id === userId);

        if (member) {
          return {
            id: userId,
            firstName: member.firstName || "Student",
            lastName: member.lastName || `#${userId}`,
          };
        }

        // If no member found, return default info
        return {
          id: userId,
          firstName: "Student",
          lastName: `#${userId}`,
        };
      } catch (error) {
        console.error("Error getting user by ID:", error);
        return {
          id: userId,
          firstName: "Unknown",
          lastName: "Student",
        };
      }
    },
    [classMembers],
  );

  // Load student grades for a specific subject
  const loadStudentGradesForSubject = useCallback(
    async (subject) => {
      if (!selectedClass || !selectedSemester) return;

      try {
        setLoading(true);

        // Use the same grades that are already loaded and filtered for the current semester
        // The grades state already contains only grades for the selected semester from loadSubjectsAndGrades
        const subjectGrades = grades.filter(
          (grade) => grade.subjectId === subject.id,
        );

        // Get user details for each grade
        const gradesWithUsers = await Promise.all(
          subjectGrades.map(async (grade) => {
            try {
              // Get user info - you might need to adjust this based on your user service
              const user = await getUserById(grade.userId);
              return {
                ...grade,
                user: user || {
                  firstName: "Unknown",
                  lastName: "Student",
                  id: grade.userId,
                },
              };
            } catch (error) {
              return {
                ...grade,
                user: {
                  firstName: "Unknown",
                  lastName: "Student",
                  id: grade.userId,
                },
              };
            }
          }),
        );

        setStudentGrades(gradesWithUsers);
        setSelectedSubject(subject);
        setShowStudentModal(true);
      } catch (error) {
        console.error("Error loading student grades:", error);
        setError("Error loading student grades for subject.");
      } finally {
        setLoading(false);
      }
    },
    [selectedClass, selectedSemester, grades, getUserById],
  );

  useEffect(() => {
    if (semesters.length > 0 && !selectedAcademicYear) {
      const years = [...new Set(semesters.map((s) => s.academicYear))].sort();
      if (years.length > 0) {
        setSelectedAcademicYear(years[years.length - 1]);
      }
    }
  }, [semesters, selectedAcademicYear]);

  // Load class statistics when parameters change
  useEffect(() => {
    if (
      showClassOverview &&
      selectedClass &&
      selectedSemester &&
      selectedAcademicYear
    ) {
      loadClassStatistics();
    }
  }, [
    showClassOverview,
    selectedClass,
    selectedSemester,
    selectedAcademicYear,
    loadClassStatistics,
  ]);

  // Format grade helper
  const formatGrade = (grade) => {
    return gradeFormatter(grade);
  };

  // Loading (initial)
  if (bootLoading) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 rounded-full border-b-2 border-sunrise-red animate-spin" />
          <span className="text-grey-dusk font-medium">
            Loading grade data…
          </span>
        </div>
      </div>
    );
  }

  // Access guard
  if (!hasAccess()) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-amber-600 text-2xl" />
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            Only administrators and instructors can manage grades.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-chart-line text-sunrise-red mr-4"></i>
                  Grade Management
                </h1>
                <p className="text-slate-cloud mt-2">
                  Manage and view student grades across schools and classes
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-sunrise-red/10 border border-sunrise-red/30 text-sunrise-red px-6 py-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle mr-2" />
                {error}
              </div>
              <button
                onClick={() => setError("")}
                className="text-sunrise-red hover:text-sunrise-600"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>
        )}

        {/* School & Class Selection */}
        <section className="card-brand p-6">
          <h3 className="text-lg font-semibold text-grey-dusk mb-4 flex items-center border-b border-pale-dawn pb-3">
            <div className="w-8 h-8 bg-slate-cloud rounded-lg flex items-center justify-center mr-3">
              <i className="fas fa-sliders-h text-white text-sm" />
            </div>
            Select Context
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                <i className="fas fa-school text-sunrise-red mr-2" />
                School
              </label>
              <select
                className="input-brand w-full"
                onChange={(e) => handleSchoolChange(e.target.value)}
                value={selectedSchool?.id || ""}
              >
                <option value="">Select a school…</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                <i className="fas fa-users text-green-cloud mr-2" />
                Class
              </label>
              <select
                className="input-brand w-full"
                onChange={(e) => handleClassChange(e.target.value)}
                value={selectedClass?.id || ""}
                disabled={!selectedSchool}
              >
                <option value="">
                  {selectedSchool ? "Select a class…" : "Select a school first"}
                </option>
                {userClasses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* School/Class Info + Semester Pills */}
        {selectedSchool && selectedClass && (
          <section className="card-brand p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="px-4 py-2 rounded-full bg-sunrise-red/10 text-sunrise-red font-semibold inline-flex items-center">
                  <i className="fas fa-school text-sunrise-red mr-2" />
                  {selectedSchool.name}
                </div>
                <div className="px-4 py-2 rounded-full bg-green-cloud/20 text-green-600 font-semibold inline-flex items-center">
                  <i className="fas fa-users text-green-cloud mr-2" />
                  {selectedClass.name}
                </div>
              </div>

              {/* Semester Pills */}
              {semesters.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {semesters.map((sem) => {
                    const active = selectedSemester?.id === sem.id;
                    return (
                      <button
                        key={sem.id}
                        onClick={() => handleSemesterClick(sem)}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition
                          ${
                            active
                              ? "bg-sunrise-red text-white border-sunrise-red"
                              : "bg-white text-grey-dusk border-pale-dawn hover:bg-pale-dawn/30"
                          }`}
                      >
                        {sem.name}
                        {sem.isCurrent ? " • Current" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Subjects & Grades */}
        {selectedSemester && (
          <section className="card-brand p-0 overflow-hidden">
            <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white p-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold flex items-center">
                  <i className="fas fa-book mr-3" />
                  {selectedSemester.name} — Subjects & Grades
                </h4>
                <div className="flex items-center gap-4">
                  <div className="px-3 py-1 rounded-full bg-white text-sunrise-600 font-semibold">
                    Semester Average: {formatGrade(semesterAverage)}
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              {subjects.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="w-20 h-20 bg-green-cloud/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-book text-green-cloud text-2xl" />
                  </div>
                  <h5 className="text-lg font-medium text-grey-dusk mb-1">
                    No Subjects Available
                  </h5>
                  <p className="text-gray-500">
                    No subjects found for this semester.
                  </p>
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-ice-white">
                    <tr className="text-left text-grey-dusk">
                      <th className="px-6 py-3 font-semibold">Subject</th>
                      <th className="px-6 py-3 font-semibold">Students</th>
                      <th className="px-6 py-3 font-semibold">Average</th>
                      <th className="px-6 py-3 font-semibold">Total Grades</th>
                      <th className="px-6 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-pale-dawn">
                    {subjects.map((subject) => {
                      const list = getSubjectGrades(subject.id);
                      const avg = calculateSubjectAverageMemo(subject.id);
                      const studentCount =
                        new Set(list.map((g) => g.userId)).size || 0;
                      return (
                        <tr key={subject.id} className="hover:bg-pale-dawn/20">
                          <td className="px-6 py-3 text-grey-dusk">
                            <div className="font-semibold">
                              {subject.subjectCode && (
                                <span className="text-sunrise-red font-bold mr-2">
                                  {subject.subjectCode}
                                </span>
                              )}
                              {subject.name}
                            </div>
                            {subject.description && (
                              <div className="text-slate-cloud text-xs mt-0.5">
                                {subject.description}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-3">
                            <span className="px-2.5 py-1 rounded-full bg-green-cloud/20 text-green-600 text-xs font-semibold">
                              {studentCount}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            {avg ? (
                              <span
                                className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-gray-100"
                                style={{ color: getGradeColor(avg) }}
                                title={`${getGradeDescription(avg)}`}
                              >
                                {avg}
                              </span>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </td>
                          <td className="px-6 py-3">
                            <span className="px-2.5 py-1 rounded-full bg-slate-cloud/20 text-slate-cloud text-xs font-semibold">
                              {list.length}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <button
                              type="button"
                              className="btn-brand-outline"
                              title="View Students with Grades"
                              onClick={() =>
                                loadStudentGradesForSubject(subject)
                              }
                            >
                              <i className="fas fa-eye mr-2" />
                              View Students
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {loading && (
              <div className="px-6 py-4 border-t border-pale-dawn bg-ice-white text-gray-600 flex items-center gap-3">
                <div className="h-5 w-5 border-2 border-sunrise-red border-t-transparent rounded-full animate-spin" />
                Loading…
              </div>
            )}
          </section>
        )}

        {/* Class Overview Section */}
        {showClassOverview && selectedSemester && selectedAcademicYear && (
          <section className="card-brand p-0 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold flex items-center">
                  <i className="fas fa-chart-bar mr-3" />
                  Class Overview - Aggregated Statistics
                </h4>
                <div className="text-sm opacity-90">
                  No personal data shown - anonymized view only
                </div>
              </div>

              {/* Academic Year Selection */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-white mb-2">
                  Academic Year:
                </label>
                <select
                  value={selectedAcademicYear}
                  onChange={(e) => setSelectedAcademicYear(e.target.value)}
                  className="px-3 py-2 rounded text-gray-800 font-medium"
                >
                  <option value="">Select academic year...</option>
                  {academicYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {classStatistics ? (
              <div className="p-6 space-y-6">
                {/* Class Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-sunrise-red/10 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <i className="fas fa-chart-bar text-sunrise-red mr-2"></i>
                      <h3 className="font-medium text-sunrise-red">
                        Class Average
                      </h3>
                    </div>
                    <p className="text-2xl font-bold text-sunrise-red">
                      {formatGrade(classStatistics.classAverage)}
                    </p>
                  </div>
                  <div className="bg-green-cloud/20 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <i className="fas fa-users text-green-cloud mr-2"></i>
                      <h3 className="font-medium text-green-600">
                        Student Count
                      </h3>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {classStatistics.studentCount}
                    </p>
                  </div>
                  <div className="bg-slate-cloud/20 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <i className="fas fa-book text-slate-cloud mr-2"></i>
                      <h3 className="font-medium text-slate-cloud">Subjects</h3>
                    </div>
                    <p className="text-2xl font-bold text-slate-cloud">
                      {classStatistics.subjectStatistics?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Subject Statistics */}
                {classStatistics.subjectStatistics &&
                  classStatistics.subjectStatistics.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Subject Performance
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Subject
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Average Grade
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Min Grade
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Max Grade
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Grade Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {classStatistics.subjectStatistics.map(
                              (subject, index) => (
                                <tr
                                  key={subject.subjectId}
                                  className={
                                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                  }
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {subject.subjectName}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatGrade(subject.averageGrade)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatGrade(subject.minGrade)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatGrade(subject.maxGrade)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {subject.gradeCount}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                {/* Anonymous Student Distribution */}
                {classStatistics.studentAverages &&
                  classStatistics.studentAverages.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Student Performance Distribution (Anonymized)
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Student #
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Average Grade
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Grade Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {classStatistics.studentAverages
                              .sort(
                                (a, b) =>
                                  (b.averageGrade || 0) - (a.averageGrade || 0),
                              )
                              .map((student, index) => (
                                <tr
                                  key={index}
                                  className={
                                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                  }
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    Student {index + 1}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatGrade(student.averageGrade)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {student.gradeCount}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <i className="fas fa-chart-bar text-4xl"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Loading Class Statistics...
                </h3>
                <p className="text-gray-600">
                  Please wait while we load the aggregated class performance
                  data.
                </p>
              </div>
            )}
          </section>
        )}

        {/* Student Grades Modal */}
        {showStudentModal && selectedSubject && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white p-6 flex justify-between items-center">
                <h3 className="text-lg font-bold">
                  <i className="fas fa-users mr-2" />
                  Students in {selectedSubject.name}
                </h3>
                <button
                  onClick={() => setShowStudentModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <i className="fas fa-times text-xl" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {studentGrades.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Grade
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Exam Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Weight
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {studentGrades.map((grade, index) => (
                          <tr
                            key={grade.id || index}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {grade.user
                                ? `${grade.user.firstName} ${grade.user.lastName}`
                                : "Unknown Student"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span
                                className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-gray-100"
                                style={{
                                  color: getGradeColor(grade.gradeValue),
                                }}
                                title={`${getGradeDescription(grade.gradeValue)}`}
                              >
                                {formatGrade(grade.gradeValue)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {grade.examDate
                                ? new Date(grade.examDate).toLocaleDateString(
                                    "de-CH",
                                  )
                                : "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {grade.weight || 1}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {grade.description || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-4">
                      <i className="fas fa-graduation-cap text-4xl" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      No Grades Found
                    </h4>
                    <p className="text-gray-600">
                      No grades have been recorded for this subject in the
                      selected semester.
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end">
                <button
                  onClick={() => setShowStudentModal(false)}
                  className="px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGrades;